import { useState } from "react";
import { Calculator, AlertTriangle, TrendingDown, Users, Clock, ArrowRight } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

export const SupportSimulator = () => {
  const { language, t } = useLanguage();

  const [values, setValues] = useState({
    ticketsPerMonth: "1000",
    agents: "3",
    hourly: "",
    hoursPerMonth: "176",
    resolvedRate: "30",
    feePerResolved: "3", 
  });

  const [result, setResult] = useState(null);

  const formatCurrency = (value) => {
    if (language === "pt") {
      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    }
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const handleChange = (field, value) => {
    const cleaned = value.replace(/[^0-9.]/g, "");
    setValues((prev) => ({ ...prev, [field]: cleaned }));
    setResult(null);
  };

  const calculate = () => {
    const ticketsPerMonth = parseFloat(values.ticketsPerMonth) || 0;
    const agents = parseFloat(values.agents) || 0;
    const hourly = parseFloat(values.hourly) || 0;
    const hoursPerMonth = parseFloat(values.hoursPerMonth) || 0;
    const resolvedRate = parseFloat(values.resolvedRate) || 0;
    const feePerResolved = parseFloat(values.feePerResolved) || 0;

    if (ticketsPerMonth <= 0 || agents <= 0 || hourly <= 0 || hoursPerMonth <= 0) return;

    const currentCost = agents * hourly * hoursPerMonth;
    const humanCostPerTicket = currentCost / ticketsPerMonth;
    const resolvedTickets = Math.floor(ticketsPerMonth * (resolvedRate / 100));
    const surgeFees = resolvedTickets * feePerResolved;
    const costIfHuman = resolvedTickets * humanCostPerTicket;
    const savings = costIfHuman - surgeFees;
    const newCost = currentCost - costIfHuman + surgeFees;

    setResult({
      currentCost: currentCost,
      newCost: newCost,
      savings: savings,
      resolvedTickets: resolvedTickets,
      surgeFees: surgeFees,
      humanCPT: humanCostPerTicket,
      aiCPT: feePerResolved
    });
  };

  return (
    <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6 lg:p-8 shadow-xl">
      {/* Cabeçalho */}
      <div className="flex items-center gap-4 mb-8">
        <div className="p-3 bg-cyan-500/10 rounded-xl border border-cyan-500/20">
          <Calculator className="w-6 h-6 text-cyan-400" />
        </div>
        <div>
          <h3 className="font-outfit text-xl font-bold text-slate-100">
            {t.supportSimulator.title}
          </h3>
          <p className="text-sm text-slate-400">{t.supportSimulator.subtitle}</p>
        </div>
      </div>

      {/* Grid de Inputs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        <div className="space-y-4">
            <div>
            <Label className="text-slate-300 text-xs uppercase tracking-wider font-semibold mb-2 block">
                {t.supportSimulator.ticketsLabel}
            </Label>
            <Input
                type="text"
                inputMode="numeric"
                value={values.ticketsPerMonth}
                onChange={(e) => handleChange("ticketsPerMonth", e.target.value)}
                className="bg-slate-950 border-slate-700 text-slate-100 h-12 focus:ring-cyan-500/50"
            />
            </div>
            <div>
            <Label className="text-slate-300 text-xs uppercase tracking-wider font-semibold mb-2 block">
                {t.supportSimulator.resolvedRateLabel}
            </Label>
            <div className="relative">
                <Input
                type="text"
                inputMode="numeric"
                value={values.resolvedRate}
                onChange={(e) => handleChange("resolvedRate", e.target.value)}
                className="bg-slate-950 border-slate-700 text-cyan-300 font-bold h-12 focus:ring-cyan-500/50"
                />
                <span className="absolute right-4 top-3.5 text-slate-500 text-sm">%</span>
            </div>
            </div>
        </div>

        <div className="space-y-4">
            <div>
                <Label className="text-slate-300 text-xs uppercase tracking-wider font-semibold mb-2 flex items-center gap-2">
                    <Users className="w-3 h-3" /> {t.supportSimulator.agentsLabel}
                </Label>
                <Input
                    type="text"
                    inputMode="numeric"
                    value={values.agents}
                    onChange={(e) => handleChange("agents", e.target.value)}
                    className="bg-slate-950 border-slate-700 text-slate-100 h-12"
                />
            </div>
            <div>
                <Label className="text-slate-300 text-xs uppercase tracking-wider font-semibold mb-2 flex items-center gap-2">
                    <Clock className="w-3 h-3" /> {t.supportSimulator.hoursLabel}
                </Label>
                <Input
                    type="text"
                    inputMode="numeric"
                    value={values.hoursPerMonth}
                    onChange={(e) => handleChange("hoursPerMonth", e.target.value)}
                    className="bg-slate-950 border-slate-700 text-slate-100 h-12"
                />
            </div>
        </div>

        <div className="space-y-4">
            <div>
                <Label className="text-slate-300 text-xs uppercase tracking-wider font-semibold mb-2 block">
                    {t.supportSimulator.hourlyLabel}
                </Label>
                <Input
                    type="text"
                    inputMode="numeric"
                    placeholder={t.supportSimulator.hourlyPlaceholder}
                    value={values.hourly}
                    onChange={(e) => handleChange("hourly", e.target.value)}
                    className="bg-slate-950 border-slate-700 text-slate-100 h-12 placeholder:text-slate-600"
                />
            </div>
            <div>
                <Label className="text-slate-300 text-xs uppercase tracking-wider font-semibold mb-2 block">
                    {t.supportSimulator.feePerResolvedLabel}
                </Label>
                <Input
                    type="text"
                    inputMode="numeric"
                    value={values.feePerResolved}
                    onChange={(e) => handleChange("feePerResolved", e.target.value)}
                    className="bg-slate-950 border-cyan-900/50 text-slate-100 h-12"
                />
            </div>
        </div>
      </div>

      <Button
        onClick={calculate}
        className="w-full mt-8 bg-gradient-to-r from-cyan-600 to-blue-600 text-white hover:from-cyan-500 hover:to-blue-500 h-14 text-lg font-bold shadow-lg shadow-cyan-900/20 transition-all duration-300 rounded-xl"
      >
        {t.supportSimulator.calculate} <ArrowRight className="ml-2 w-5 h-5" />
      </Button>

      {/* Resultados */}
      {result && (
        <div className="mt-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="bg-slate-950 rounded-xl border border-slate-800 p-5 mb-6 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-1 h-full bg-cyan-500"></div>
            <p className="text-xs text-slate-400 uppercase tracking-widest font-semibold mb-4">
                {t.supportSimulator.ticketCostComparison}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                <div className="flex-1 w-full p-3 bg-red-500/5 border border-red-500/10 rounded-lg flex justify-between items-center">
                    <span className="text-sm text-red-300 font-medium">{t.supportSimulator.humanCost}</span>
                    <span className="text-lg font-bold text-red-200">
                        {formatCurrency(result.humanCPT)} <span className="text-xs font-normal opacity-70">/ticket</span>
                    </span>
                </div>

                <div className="hidden sm:block text-slate-600"><ArrowRight className="w-5 h-5"/></div>

                <div className="flex-1 w-full p-3 bg-emerald-500/5 border border-emerald-500/20 rounded-lg flex justify-between items-center relative">
                    <div className="absolute -top-2 -right-2 bg-emerald-500 text-slate-950 text-[10px] font-bold px-2 py-0.5 rounded-full">
                        WINNER
                    </div>
                    <span className="text-sm text-emerald-300 font-medium">{t.supportSimulator.aiCost}</span>
                    <span className="text-lg font-bold text-emerald-300">
                        {formatCurrency(result.aiCPT)} <span className="text-xs font-normal opacity-70">/ticket</span>
                    </span>
                </div>
            </div>
          </div>

          <div className="bg-slate-950/50 border border-slate-800 rounded-xl p-6 space-y-4">
            <div className="flex items-center justify-between pb-4 border-b border-slate-800">
              <span className="text-slate-400">{t.supportSimulator.currentCostLabel}</span>
              <span className="text-slate-300 font-medium">{formatCurrency(result.currentCost)}</span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-slate-400">{t.supportSimulator.resolvedTicketsLabel}</span>
              <span className="text-cyan-200 font-medium">{result.resolvedTickets} tickets</span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-slate-400">{t.supportSimulator.surgeFeesLabel}</span>
              <span className="text-slate-300 font-medium">{formatCurrency(result.surgeFees)}</span>
            </div>

            <div className="pt-4 mt-2 border-t border-slate-800 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TrendingDown className="w-5 h-5 text-emerald-400" />
                <span className="text-emerald-400 font-bold text-lg">{t.supportSimulator.savingsLabel}</span>
              </div>
              <span className="text-2xl font-bold text-emerald-400 tracking-tight">
                {formatCurrency(result.savings)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Premissas (Harmonizado com a outra calculadora) */}
      <div className="mt-6 p-4 bg-slate-950/40 border border-slate-800 rounded-xl">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-slate-400 mb-1">
              {language === 'pt' ? 'Premissas do cálculo' : 'Calculation assumptions'}
            </p>
            <ul className="text-xs text-slate-500 space-y-1 list-disc pl-3">
              <li>
                {language === 'pt' 
                  ? 'A "economia" reflete o custo de oportunidade (tempo humano liberado).' 
                  : '"Savings" reflect opportunity cost (freed up human time).'}
              </li>
              <li>
                {language === 'pt' 
                  ? 'O custo por hora deve incluir encargos e benefícios para precisão.' 
                  : 'Hourly cost should include taxes and benefits for accuracy.'}
              </li>
              <li>
                {language === 'pt' 
                  ? 'Estimativa simplificada. Não garantimos redução de headcount.' 
                  : 'Simplified estimate. We do not guarantee headcount reduction.'}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
